// ASCII DXF writer (R12 / AC1009 dialect: maximum compatibility with laser,
// CNC, and CAD importers; no entity handles required). Untouched circular
// holes are CIRCLE entities; every other outline is a closed POLYLINE.
// Units are millimetres. Layers: HOLES for cut outlines, BOUNDARY for the
// optional panel outline. The pipeline guarantees the outlines are unioned
// and disjoint before they reach this writer.

import type { GenResult } from '../core/types'

function fmt(v: number): string {
  const r = Math.round(v * 1e6) / 1e6
  return String(r)
}

class Dxf {
  private out: string[] = []

  tag(code: number, value: string | number) {
    this.out.push(String(code), typeof value === 'number' ? fmt(value) : value)
  }

  text(): string {
    return this.out.join('\r\n') + '\r\n'
  }
}

function layerTable(d: Dxf, layers: string[]) {
  d.tag(0, 'TABLE')
  d.tag(2, 'LAYER')
  d.tag(70, layers.length)
  for (const name of layers) {
    d.tag(0, 'LAYER')
    d.tag(2, name)
    d.tag(70, 0)
    d.tag(62, 7) // default color
    d.tag(6, 'CONTINUOUS')
  }
  d.tag(0, 'ENDTAB')
}

function circle(d: Dxf, layer: string, cx: number, cy: number, r: number) {
  d.tag(0, 'CIRCLE')
  d.tag(8, layer)
  d.tag(10, cx)
  d.tag(20, cy)
  d.tag(30, 0)
  d.tag(40, r)
}

function closedPolyline(d: Dxf, layer: string, ring: [number, number][]) {
  d.tag(0, 'POLYLINE')
  d.tag(8, layer)
  d.tag(66, 1) // vertices follow
  d.tag(70, 1) // closed
  for (const [x, y] of ring) {
    d.tag(0, 'VERTEX')
    d.tag(8, layer)
    d.tag(10, x)
    d.tag(20, y)
    d.tag(30, 0)
  }
  d.tag(0, 'SEQEND')
}

export function exportDxf(result: GenResult, includeBoundary: boolean): string {
  const d = new Dxf()

  d.tag(0, 'SECTION')
  d.tag(2, 'HEADER')
  d.tag(9, '$ACADVER')
  d.tag(1, 'AC1009')
  d.tag(9, '$INSUNITS')
  d.tag(70, 4) // millimetres
  d.tag(9, '$MEASUREMENT')
  d.tag(70, 1) // metric
  d.tag(0, 'ENDSEC')

  d.tag(0, 'SECTION')
  d.tag(2, 'TABLES')
  layerTable(d, includeBoundary ? ['HOLES', 'BOUNDARY'] : ['HOLES'])
  d.tag(0, 'ENDSEC')

  d.tag(0, 'SECTION')
  d.tag(2, 'ENTITIES')
  for (const hole of result.holes) {
    if (hole.circle) {
      circle(d, 'HOLES', hole.circle.cx, hole.circle.cy, hole.circle.r)
    } else {
      for (const ring of hole.poly) closedPolyline(d, 'HOLES', ring)
    }
  }
  if (includeBoundary) {
    if (result.boundaryCircle) {
      circle(d, 'BOUNDARY', result.boundaryCircle.cx, result.boundaryCircle.cy, result.boundaryCircle.r)
    } else {
      closedPolyline(d, 'BOUNDARY', result.boundary)
    }
  }
  d.tag(0, 'ENDSEC')
  d.tag(0, 'EOF')

  return d.text()
}
